import { useCallback, useContext } from 'react';
import useHttp from './useHttp';
import AuthContext from '../context/authContext';

// Read-only executive price overview aggregated per location, plus editing helpers.
const usePriceOverview = () => {
  const authCtx = useContext(AuthContext);
  const { isLoading, error, sendRequest } = useHttp();

  const getPriceOverview = useCallback(async () => {
    return await sendRequest({
      url: '/api/v1/product/price-overview',
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
    });
  }, [authCtx, sendRequest]);

  // Impact analysis for a price list: which locations/entertainments book it.
  const getPricingImpact = useCallback(
    async (productId, pricingId) => {
      return await sendRequest({
        url: `/api/v1/product/${productId}/pricing/${pricingId}/impact`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
    },
    [authCtx, sendRequest]
  );

  // Update a single price row in place (affects every usage of the price list).
  const updatePrice = useCallback(
    async (productId, pricingId, entryId, priceId, price) => {
      return await sendRequest({
        url: `/api/v1/product/${productId}/pricing/${pricingId}/priceMatrix/${entryId}/price/${priceId}`,
        method: 'PATCH',
        headers: authCtx.getAuthHeaders(),
        body: { price },
      });
    },
    [authCtx, sendRequest]
  );

  // Decouple price changes into a new bookable list, archiving the old one.
  const decouplePricing = useCallback(
    async (productId, pricingId, { changes, newListName }) => {
      return await sendRequest({
        url: `/api/v1/product/${productId}/pricing/${pricingId}/decouple`,
        method: 'POST',
        headers: authCtx.getAuthHeaders(),
        body: { changes, newListName },
      });
    },
    [authCtx, sendRequest]
  );

  // Update a per-product upgrade price override (package/upsell/singleOffer).
  const updateUpgradePrice = useCallback(
    async (productId, { kind, upgradeId, priceOverride }) => {
      return await sendRequest({
        url: `/api/v1/product/${productId}/upgradePrice`,
        method: 'PATCH',
        headers: authCtx.getAuthHeaders(),
        body: { kind, upgradeId, priceOverride },
      });
    },
    [authCtx, sendRequest]
  );

  // Impact of a global upgrade base-price change: which products use the upgrade.
  const getUpgradeImpact = useCallback(
    async upgradeId => {
      return await sendRequest({
        url: `/api/v1/product/upgrade-impact/${upgradeId}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
    },
    [authCtx, sendRequest]
  );

  // Update the global upgrade base price (affects every product without an override).
  const updateUpgradeGlobalPrice = useCallback(
    async (upgradeId, price) => {
      return await sendRequest({
        url: `/api/v1/upgrade/${upgradeId}`,
        method: 'PATCH',
        headers: authCtx.getAuthHeaders(),
        body: { price },
      });
    },
    [authCtx, sendRequest]
  );

  return {
    isLoading,
    error,
    getPriceOverview,
    getPricingImpact,
    updatePrice,
    decouplePricing,
    updateUpgradePrice,
    getUpgradeImpact,
    updateUpgradeGlobalPrice,
  };
};

export default usePriceOverview;
